/**
 * Invoice Analysis Configuration
 *
 * Simplified config structure with two orthogonal concerns:
 * 1. ODIE vs Standard mode (org-level) - Controls UI: coverage vs exposure, vendor field, summary
 * 2. Recommendation vs DB data source - Controls where data comes from
 */
import type { InvoiceRecommendation } from '~/components/types/invoice-types';
import { SaveInvoiceRequestInvoiceProcessingType } from '~/server_shared/openapi/data-contracts';

import { mapInvoiceToInvoiceData, mapLineItemToLineItemProps } from '../invoices/invoiceUtils';

import type {
  InvoiceAnalysisConfig,
  InvoiceDataConfig,
  InvoiceSaveConfig,
  InvoiceUIConfig,
  InvoiceValidationConfig,
} from './invoiceAnalysisConfig.types';

// Re-export types for consumers
export type {
  ClaimDocument,
  InvoiceAnalysisConfig,
  InvoiceDataConfig,
  InvoiceSaveConfig,
  InvoiceUIConfig,
  InvoiceValidationConfig,
} from './invoiceAnalysisConfig.types';

// ============================================================================
// UI Configs - Determined by ODIE mode (org-level setting)
// ============================================================================

const odieUIConfig: InvoiceUIConfig = {
  entityColumn: 'coverage',
  vendorFieldType: 'text',
  showCoverageSummary: true,
  getTitle: () => 'Clive Invoice Analysis',
};

const standardUIConfig: InvoiceUIConfig = {
  entityColumn: 'exposure',
  vendorFieldType: 'contact',
  showCoverageSummary: false,
  getTitle: (documentName) => `Document - ${documentName} analysis`,
};

// ============================================================================
// Validation Configs - Determined by ODIE mode
// ============================================================================

const odieValidationConfig: InvoiceValidationConfig = {
  validateEntitySelection: (item) => item.coverage_decision !== 'covered' || !!item.coverage,
  entityValidationMessage: 'Please select coverage for all covered line items',
};

const standardValidationConfig: InvoiceValidationConfig = {
  validateEntitySelection: (item) => item.coverage_decision !== 'covered' || !!item.exposure_id,
  entityValidationMessage: 'Please select exposure for all covered line items',
};

// ============================================================================
// Data Configs - Determined by data source (recommendation vs DB)
// ============================================================================

const dbDataConfig: InvoiceDataConfig = {
  getInvoiceData: ({ document, invoice }) => {
    if (invoice) {
      return mapInvoiceToInvoiceData(document, invoice);
    }
    return {
      date: document.document_date || '',
      number: '',
      total_amount: undefined,
      document_claim_internal_id: document.claim_internal_id,
    };
  },

  getLineItems: ({ invoice }) => {
    if (!invoice) return [];
    return invoice.line_items.map((item) => ({
      ...mapLineItemToLineItemProps(item),
      exposure_id: item.exposure_id || undefined,
      // Per-item currency takes priority; fall back to deprecated invoice-level currency
      amount_currency: item.amount_currency || invoice.invoice_currency || undefined,
      invoice_date: invoice.invoice_date || undefined,
    }));
  },
};

const recommendationDataConfig: InvoiceDataConfig = {
  getInvoiceData: ({ document, invoiceRecommendation }) => {
    if (!invoiceRecommendation) {
      throw new Error('Recommendation data config requires invoiceRecommendation');
    }
    return {
      date: invoiceRecommendation.invoice_date || document.document_date || '',
      number: invoiceRecommendation.invoice_number || '',
      vendor: invoiceRecommendation.vendor || undefined,
      document_claim_internal_id: invoiceRecommendation.document_claim_internal_id || document.claim_internal_id,
    };
  },

  getLineItems: ({ invoiceRecommendation, coverageMap, policyCurrency }) => {
    if (!invoiceRecommendation) return [];
    return invoiceRecommendation.line_items.map((item) => ({
      id: item.id,
      description: item.description,
      amount: item.amount,
      covered_amount: item.covered_amount || undefined,
      coverage_decision: item.coverage_decision || undefined,
      coverage: coverageMap[item.coverage_key || ''] ? item.coverage_key || '' : '',
      exposure_id: item.exposure_id ?? undefined,
      explanation: item.explanation || undefined,
      payable_type: 'indemnity' as const,
      item_date: item.item_date || undefined,
      // Mirror the "Add Item" / DB-loaded behavior: default unset currency to the policy's
      // currency so hidden-column orgs (and the validator) get a sensible value.
      amount_currency: policyCurrency,
    }));
  },
};

// ============================================================================
// Save Config Builder - Depends on both ODIE mode and data source
// ============================================================================

function buildSaveConfig(isOdieMode: boolean, useRecommendationData: boolean): InvoiceSaveConfig {
  return {
    // ODIE or recommendation uses OdieInvoice, otherwise GenericInvoice
    processingType:
      isOdieMode || useRecommendationData
        ? SaveInvoiceRequestInvoiceProcessingType.OdieInvoice
        : SaveInvoiceRequestInvoiceProcessingType.GenericInvoice,
    // DB-backed returns invoice ID for updates, recommendation creates new
    getInvoiceId: useRecommendationData ? () => undefined : (invoice) => invoice?.id,
    // ODIE maps coverage key, standard doesn't
    mapLineItemCoverageKey: isOdieMode ? (item) => item.coverage || null : () => null,
  };
}

// ============================================================================
// Factory Function
// ============================================================================

export function getInvoiceAnalysisConfig(
  invoiceRecommendation: InvoiceRecommendation | undefined,
  isOdieMode: boolean,
  hasExistingInvoice = false
): InvoiceAnalysisConfig {
  // Use recommendation data only if we have one AND no existing invoice (preserves user edits)
  const useRecommendationData = !!invoiceRecommendation && !hasExistingInvoice;

  return {
    mode: isOdieMode ? 'odie' : 'standard',
    dataSource: useRecommendationData ? 'recommendation' : 'db',
    data: useRecommendationData ? recommendationDataConfig : dbDataConfig,
    ui: isOdieMode ? odieUIConfig : standardUIConfig,
    save: buildSaveConfig(isOdieMode, useRecommendationData),
    validation: isOdieMode ? odieValidationConfig : standardValidationConfig,
  };
}
